import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthActions, authFeature } from '../../features/auth/state';
import { hasAnyPermission } from '../auth/auth-permissions.util';
import { BackofficeRole, normalizeBackofficeRoles } from '../auth/role-access.util';
import { AuthSessionSyncService } from '../services/auth-session-sync.service';

/**
 * Permission-first: decisions must branch on `permissions`, never solely on
 * role names, so the backend can reorganize areas without a frontend deploy.
 *
 * Precedence:
 *   1. Route declares neither `roles` nor `permissions` -> allow.
 *   2. Management bypass (root/administrative) -> allow.
 *   3. Route declares `permissions` and the user holds ANY of them -> allow,
 *      sufficient on its own, no role match required.
 *   4. Otherwise fall back to role-name intersection, keeping unmigrated
 *      routes (declaring only `roles`) working.
 *   5. Otherwise redirect to /access-denied.
 */
export const roleGuard: CanActivateFn = (route) => {
  const store = inject(Store);
  const router = inject(Router);
  const sessionSync = inject(AuthSessionSyncService);

  const allowedRoles = (route.data?.['roles'] ?? []) as BackofficeRole[];
  const requiredPermissions = (route.data?.['permissions'] ?? []) as string[];

  if (!allowedRoles.length && !requiredPermissions.length) {
    return true;
  }

  const legacyRole = store.selectSignal(authFeature.selectRole);
  const roles = store.selectSignal(authFeature.selectRoles);
  const permissions = store.selectSignal(authFeature.selectPermissions);

  const session = sessionSync.readSession();

  if (!legacyRole() && !roles().length && session.token) {
    store.dispatch(AuthActions.initSuccess(session));
  }

  const activeLegacyRole = legacyRole() ?? session.role;
  const activeRoles = roles().length ? roles() : session.roles;
  const activePermissions = permissions().length ? permissions() : session.permissions;

  const allRoleNames = Array.from(
    new Set([...activeRoles, ...(activeLegacyRole ? [activeLegacyRole] : [])]),
  );

  // Management bypass: mirrors the API, where SuperUser and Administrative
  // skip every permission check. Diverging here would lock admins out of
  // screens whose permission they do not literally hold.
  const normalizedUserRoles = normalizeBackofficeRoles(allRoleNames);

  if (normalizedUserRoles.includes('root') || normalizedUserRoles.includes('administrative')) {
    return true;
  }

  if (requiredPermissions.length && hasAnyPermission(activePermissions, requiredPermissions)) {
    return true;
  }

  if (allowedRoles.length && allowedRoles.some((role) => normalizedUserRoles.includes(role))) {
    return true;
  }

  return router.createUrlTree(['/access-denied'], {
    queryParams: {
      role: allRoleNames.length ? allRoleNames.join(',') : 'none',
      required: allowedRoles.join(','),
    },
  });
};
